/**
 * Re-wrap al DEK-ului pentru toți userii care au share pe o notiță.
 *
 * Fiecare salvare generează un DEK nou; dacă îl împachetăm doar pentru userul
 * care salvează, ceilalți rămân cu un wrap pe DEK-ul vechi și nu mai pot
 * decripta notița. Orice cale care rescrie conținutul (save, restore versiune,
 * upload de atașamente) trebuie să treacă pe aici.
 */
import { notesApi } from '@/api/notes';
import { tenantsApi } from '@/api/tenants';
import { base64ToBytes, bytesToBase64, wrapKey, type KeyPair } from '@/crypto/sodium';
import i18n from '@/i18n';

export interface NoteDekWraps {
  /** {user_id → DEK wrapped (base64)} pentru fiecare share curent. */
  dek_wraps: Record<string, string>;
  /** Wrap-ul apelantului (prezent și în `dek_wraps` dacă are share). */
  dek_wrapped: string;
}

export async function wrapDekForNoteShares(args: {
  noteId: string;
  tenantId: string;
  dek: Uint8Array;
  keyPair: KeyPair;
  userId: string | undefined;
}): Promise<NoteDekWraps> {
  const { noteId, tenantId, dek, keyPair, userId } = args;
  const [shares, members] = await Promise.all([
    notesApi.listShares(noteId),
    tenantsApi.listMembers(tenantId),
  ]);

  const dek_wraps: Record<string, string> = {};
  let mine = '';
  for (const share of shares) {
    const member = members.find((m) => m.user_id === share.user_id);
    if (!member) {
      throw new Error(
        i18n.t('notes_ui.shares.wrap_member_missing', { userId: share.user_id }),
      );
    }
    const wrapped = bytesToBase64(await wrapKey(dek, base64ToBytes(member.public_key)));
    dek_wraps[share.user_id] = wrapped;
    if (share.user_id === userId) mine = wrapped;
  }
  if (!mine) {
    // Apelantul nu e în shares (admin forțat?) — wrap separat.
    mine = bytesToBase64(await wrapKey(dek, keyPair.publicKey));
  }
  return { dek_wraps, dek_wrapped: mine };
}
